import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDate, shortId } from '../lib/format';

export interface SupportCaseSummary {
  uuid: string;
  email: string;
  name: string | null;
  subject: string;
  category: string;
  status: string;
  priority: string;
  channel: string;
  updated_at: string;
  last_customer_message_at: string | null;
  last_admin_reply_at: string | null;
}

export const STATUS_COLORS: Record<string, string> = {
  open: '#b45309',
  pending: '#2563eb',
  resolved: '#15803d',
  closed: '#6b7280',
};

/** Fetches /api/admin/support with `params` and renders the case rows. */
export function SupportCasesTable({
  params,
  emptyText = 'No support cases.',
}: {
  params: URLSearchParams;
  emptyText?: string;
}) {
  const [cases, setCases] = useState<SupportCaseSummary[] | null>(null);
  const key = params.toString();

  useEffect(() => {
    api
      .get<{ cases: SupportCaseSummary[] }>(`/api/admin/support?${key}`)
      .then((d) => setCases(d.cases))
      .catch(console.error);
  }, [key]);

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
      <div style={{ ...rowStyle, fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>
        <span>Case</span>
        <span>Subject</span>
        <span>Customer</span>
        <span>Status</span>
        <span>Updated</span>
      </div>
      {cases === null && <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>Loading…</p>}
      {cases?.length === 0 && <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>{emptyText}</p>}
      {cases?.map((sc) => {
        // Customer spoke last → needs a response.
        const awaitingUs =
          sc.status === 'open' &&
          (!sc.last_admin_reply_at ||
            (sc.last_customer_message_at ?? '') > sc.last_admin_reply_at);
        return (
          <Link key={sc.uuid} to={`/support/${sc.uuid}`} style={rowStyle}>
            <span style={{ fontFamily: 'monospace', fontSize: 13 }}>
              #{shortId(sc.uuid)}
              {sc.priority === 'high' && <span style={{ color: '#b91c1c', marginLeft: 6 }}>!</span>}
            </span>
            <span style={{ fontSize: 14, fontWeight: awaitingUs ? 600 : 400 }}>
              {sc.subject}
              <span style={{ display: 'block', fontSize: 12, color: '#9ca3af', fontWeight: 400 }}>
                {sc.category} · {sc.channel}
              </span>
            </span>
            <span style={{ fontSize: 14 }}>{sc.name || sc.email}</span>
            <span style={{ fontSize: 13, color: STATUS_COLORS[sc.status] || '#6b7280' }}>{sc.status}</span>
            <span style={{ fontSize: 13, color: '#6b7280' }}>{formatDate(sc.updated_at)}</span>
          </Link>
        );
      })}
    </div>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '110px 1fr 200px 90px 160px',
  gap: 16,
  padding: '14px 20px',
  borderBottom: '1px solid #f0f0f0',
  alignItems: 'center',
  textDecoration: 'none',
  color: 'inherit',
};
